import React, { useState, useEffect } from 'react';
import vaultPricingService from '../services/vaultPricingService';
import './VaultPricing.css';
import CurrencySelect from './CurrencySelect';


const INTERVALS = [
    { label: 'Monthly', interval: 'month', interval_count: 1 },
    { label: 'Every 3 months', interval: 'month', interval_count: 3 },
    { label: 'Every 6 months', interval: 'month', interval_count: 6 },
    { label: 'Annually', interval: 'year', interval_count: 1 },
    { label: 'Every 2 years', interval: 'year', interval_count: 2 },
    { label: 'Every 3 years', interval: 'year', interval_count: 3 },
    { label: 'One-time', interval: null, interval_count: null },
];

// Default product extensions keyed by type
const DEFAULT_PRODUCTS = {
    upfront: { ext: 'default-1yr-upfront', label: 'Fixed Price Upfront', interval: null, interval_count: null },
    monthly: { ext: 'default-1mo-reoccuring', label: 'Monthly Recurring', interval: 'month', interval_count: 1 },
    annual:  { ext: 'default-1yr-reoccuring', label: 'Annual Recurring',  interval: 'year',  interval_count: 1 },
};

const formatPrice = (unit_amount, currency) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(unit_amount / 100);

const intervalLabel = (recurring) => {
    if (!recurring) return 'one-time';
    const { interval, interval_count } = recurring;
    if (interval_count === 1) return `/ ${interval}`;
    return `/ ${interval_count} ${interval}s`;
};

const EMPTY_PRODUCT = { name: '', description: '' };
const EMPTY_PRICE = { productId: '', amount: '', currency: 'EUR', interval: 'year', interval_count: 1, nickname: '', lookup_key: '' };

const VaultPricing = () => {
    const [tab, setTab] = useState('default');
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState(null);
    const [saving, setSaving] = useState(false);

    const [config, setConfig] = useState({ price_prefix: '' });
    const [configDraft, setConfigDraft] = useState('');
    const [configSaving, setConfigSaving] = useState(false);

    const [productModal, setProductModal] = useState(null);
    const [priceModal, setPriceModal] = useState(null);
    const [archivingPriceId, setArchivingPriceId] = useState(null);
    const [archivingProductId, setArchivingProductId] = useState(null);
    const [creatingProductKey, setCreatingProductKey] = useState(null);

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        setMsg(null);
        try {
            const [productsRes, configRes] = await Promise.all([
                vaultPricingService.listProducts(),
                vaultPricingService.getConfig(),
            ]);
            setProducts(productsRes.data?.products || []);
            const cfg = configRes.data?.config || { price_prefix: '' };
            setConfig(cfg);
            setConfigDraft(cfg.price_prefix);
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error?.message || 'Failed to load' });
        } finally {
            setLoading(false);
        }
    };

    const flash = (text, type = 'success') => {
        setMsg({ type, text });
        setTimeout(() => setMsg(null), 4000);
    };

    // ─── Config ───────────────────────────────────────────────────────────────

    const saveConfig = async () => {
        setConfigSaving(true);
        try {
            const res = await vaultPricingService.updateConfig({ price_prefix: configDraft });
            const cfg = res.data?.config;
            setConfig(cfg);
            setConfigDraft(cfg.price_prefix);
            flash('Configuration saved');
        } catch (err) {
            flash(err.response?.data?.error?.message || 'Failed to save configuration', 'error');
        } finally {
            setConfigSaving(false);
        }
    };

    // ─── Products ─────────────────────────────────────────────────────────────

    const openAddProduct = () => setProductModal({ mode: 'add', ...EMPTY_PRODUCT });
    const openEditProduct = (p) => setProductModal({ mode: 'edit', id: p.id, name: p.name, description: p.description || '' });

    const saveProduct = async () => {
        if (!productModal.name.trim()) return;
        setSaving(true);
        try {
            if (productModal.mode === 'add') {
                const res = await vaultPricingService.createProduct({ name: productModal.name, description: productModal.description });
                setProducts(prev => [...prev, res.data.product]);
            } else {
                const res = await vaultPricingService.updateProduct(productModal.id, { name: productModal.name, description: productModal.description });
                setProducts(prev => prev.map(p => p.id === productModal.id ? { ...p, ...res.data.product } : p));
            }
            setProductModal(null);
            flash(productModal.mode === 'add' ? 'Product created' : 'Product updated');
        } catch (err) {
            flash(err.response?.data?.error?.message || 'Failed to save product', 'error');
        } finally {
            setSaving(false);
        }
    };

    const archiveProduct = async (productId) => {
        if (!window.confirm('Archive this product? It will no longer appear in Stripe or the payment screen.')) return;
        setArchivingProductId(productId);
        try {
            await vaultPricingService.archiveProduct(productId);
            setProducts(prev => prev.filter(p => p.id !== productId));
            flash('Product archived');
        } catch (err) {
            flash(err.response?.data?.error?.message || 'Failed to archive product', 'error');
        } finally {
            setArchivingProductId(null);
        }
    };

    // Create a default product directly in Stripe (no modal — name is fixed)
    const createDefaultProduct = async (key) => {
        const defn = DEFAULT_PRODUCTS[key];
        const name = `${config.price_prefix}${defn.ext}`;
        setCreatingProductKey(key);
        try {
            const res = await vaultPricingService.createProduct({ name });
            setProducts(prev => [...prev, res.data.product]);
            flash(`Product "${name}" created`);
        } catch (err) {
            flash(err.response?.data?.error?.message || 'Failed to create product', 'error');
        } finally {
            setCreatingProductKey(null);
        }
    };

    // ─── Prices ───────────────────────────────────────────────────────────────

    const openAddPrice = (productId) => setPriceModal({ mode: 'add', productId, ...EMPTY_PRICE, productId });

    // Open add-price modal pre-configured for a default product slot
    const openAddDefaultPrice = (productId, key) => {
        const defn = DEFAULT_PRODUCTS[key];
        setPriceModal({
            mode: 'add',
            productId,
            amount: '',
            currency: 'EUR',
            interval: defn.interval,
            interval_count: defn.interval_count,
            nickname: '',
            lookup_key: `${config.price_prefix}${defn.ext}`,
            isDefault: true,
        });
    };

    const openEditPrice = (productId, price) => setPriceModal({
        mode: 'edit', productId, id: price.id,
        nickname: price.nickname || '',
    });

    const savePrice = async () => {
        setSaving(true);
        try {
            if (priceModal.mode === 'add') {
                const selectedInterval = INTERVALS.find(i => i.interval === priceModal.interval && i.interval_count === parseInt(priceModal.interval_count));
                const res = await vaultPricingService.createPrice({
                    productId: priceModal.productId,
                    amount: priceModal.amount,
                    currency: priceModal.currency,
                    ...(selectedInterval?.interval ? { interval: selectedInterval.interval, interval_count: selectedInterval.interval_count } : {}),
                    nickname: priceModal.nickname || undefined,
                    lookup_key: priceModal.lookup_key || undefined,
                });
                setProducts(prev => prev.map(p =>
                    p.id === priceModal.productId
                        ? { ...p, prices: [...p.prices, res.data.price].sort((a, b) => (a.unit_amount || 0) - (b.unit_amount || 0)) }
                        : p
                ));
            } else {
                const res = await vaultPricingService.updatePrice(priceModal.id, { nickname: priceModal.nickname });
                setProducts(prev => prev.map(p =>
                    p.id === priceModal.productId
                        ? { ...p, prices: p.prices.map(pr => pr.id === priceModal.id ? { ...pr, ...res.data.price } : pr) }
                        : p
                ));
            }
            setPriceModal(null);
            flash(priceModal.mode === 'add' ? 'Price created' : 'Price updated');
        } catch (err) {
            flash(err.response?.data?.error?.message || 'Failed to save price', 'error');
        } finally {
            setSaving(false);
        }
    };

    const archivePrice = async (productId, priceId) => {
        if (!window.confirm('Archive this price? It will no longer be available for new vaults.')) return;
        setArchivingPriceId(priceId);
        try {
            await vaultPricingService.archivePrice(priceId);
            setProducts(prev => prev.map(p =>
                p.id === productId ? { ...p, prices: p.prices.filter(pr => pr.id !== priceId) } : p
            ));
            flash('Price archived');
        } catch (err) {
            flash(err.response?.data?.error?.message || 'Failed to archive price', 'error');
        } finally {
            setArchivingPriceId(null);
        }
    };

    // ─── Helpers ──────────────────────────────────────────────────────────────

    const findDefaultProduct = (key) => {
        if (!config.price_prefix) return null;
        const name = `${config.price_prefix}${DEFAULT_PRODUCTS[key].ext}`;
        return products.find(p => p.name === name) || null;
    };

    const renderPricesTable = (product, defaultKey) => (
        <div className="prices-section">
            <div className="prices-header">
                <span className="prices-label">Prices</span>
                <button
                    className="btn-add-price"
                    onClick={() => defaultKey ? openAddDefaultPrice(product.id, defaultKey) : openAddPrice(product.id)}
                >
                    + Add Price
                </button>
            </div>
            {product.prices.length === 0 ? (
                <div className="no-prices">No prices configured.</div>
            ) : (
                <table className="prices-table">
                    <thead>
                        <tr>
                            <th>Amount</th>
                            <th>Billing</th>
                            <th>Nickname</th>
                            <th>Lookup Key</th>
                            <th>Price ID</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {product.prices.map(price => (
                            <tr key={price.id} className={archivingPriceId === price.id ? 'price-row-archiving' : ''}>
                                <td className="price-amount">{formatPrice(price.unit_amount, price.currency)}</td>
                                <td className="price-interval">{intervalLabel(price.recurring)}</td>
                                <td>{price.nickname || <span className="muted">—</span>}</td>
                                <td className="price-lookup-key">{price.lookup_key || <span className="muted">—</span>}</td>
                                <td className="price-id">{price.id}</td>
                                <td className="price-row-actions">
                                    <button className="btn-secondary btn-sm" onClick={() => openEditPrice(product.id, price)} disabled={archivingPriceId === price.id}>Edit</button>
                                    <button className="btn-danger btn-sm" onClick={() => archivePrice(product.id, price.id)} disabled={archivingPriceId === price.id}>
                                        {archivingPriceId === price.id ? 'Archiving...' : 'Archive'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );

    const renderDefaultProductSlot = (key) => {
        const defn = DEFAULT_PRODUCTS[key];
        const product = findDefaultProduct(key);
        const fullName = config.price_prefix ? `${config.price_prefix}${defn.ext}` : `{prefix}${defn.ext}`;

        return (
            <div className="default-product-slot" key={key}>
                <div className="default-product-name">{fullName}</div>
                {!product ? (
                    <div className="default-product-missing">
                        <span className="muted">Not yet created in Stripe.</span>
                        <button
                            className="btn-primary btn-sm"
                            onClick={() => createDefaultProduct(key)}
                            disabled={!config.price_prefix || creatingProductKey === key}
                        >
                            {creatingProductKey === key ? 'Creating...' : 'Create in Stripe'}
                        </button>
                    </div>
                ) : (
                    renderPricesTable(product, key)
                )}
            </div>
        );
    };

    if (loading) return <div className="loading">Loading...</div>;

    return (
        <div className="vault-pricing-container">
            <div className="vault-pricing-header">
                <div>
                    <h1>Vault Pricing</h1>
                    <p className="subtitle">Manage Stripe products and prices for vault subscriptions.</p>
                </div>
                {tab === 'stripe' && (
                    <button className="btn-primary" onClick={openAddProduct}>+ Add Product</button>
                )}
            </div>

            {msg && <div className={`msg-banner msg-${msg.type}`}>{msg.text}</div>}

            <div className="tabs">
                <button className={`tab-btn${tab === 'default' ? ' active' : ''}`} onClick={() => setTab('default')}>Default Vault Pricing</button>
                <button className={`tab-btn${tab === 'stripe' ? ' active' : ''}`} onClick={() => setTab('stripe')}>Stripe Products &amp; Prices</button>
                <button className={`tab-btn${tab === 'config' ? ' active' : ''}`} onClick={() => setTab('config')}>Pricing Configuration</button>
            </div>

            {/* ── Default Vault Pricing tab ──────────────────────────────────── */}
            {tab === 'default' && (
                <div>
                    {!config.price_prefix && (
                        <div className="config-notice">
                            Set a <button className="link-btn" onClick={() => setTab('config')}>price prefix</button> in Pricing Configuration before creating default products.
                        </div>
                    )}

                    <div className="default-section">
                        <div className="default-section-header">
                            <h3>Fixed Price Upfront <span className="billing-badge badge-invoice">Invoice / Bank Transfer</span></h3>
                            <p className="default-section-note">
                                When a customer pays by invoice, the unit price is multiplied by the number of retention years.
                                The invoice shows the retention period as the quantity and the annual price as the unit amount.
                            </p>
                        </div>
                        {renderDefaultProductSlot('upfront')}
                    </div>

                    <div className="default-section">
                        <div className="default-section-header">
                            <h3>Recurring Billing <span className="billing-badge badge-card">Credit Card</span></h3>
                            <p className="default-section-note">
                                Selecting a recurring price creates a Stripe subscription for the customer.
                                Choose between monthly or annual billing cycles.
                            </p>
                        </div>
                        <div className="recurring-label">Annual</div>
                        {renderDefaultProductSlot('annual')}
                        <div className="recurring-label recurring-label-spaced">Monthly</div>
                        {renderDefaultProductSlot('monthly')}
                    </div>
                </div>
            )}

            {/* ── Stripe Products & Prices tab ──────────────────────────────── */}
            {tab === 'stripe' && (
                <div>
                    {products.length === 0 && (
                        <div className="empty-state">No products yet. Add one to get started.</div>
                    )}
                    {products.map(product => (
                        <div key={product.id} className="product-card">
                            <div className="product-card-header">
                                <div className="product-info">
                                    <div className="product-name">{product.name}</div>
                                    {product.description && <div className="product-description">{product.description}</div>}
                                    <div className="product-id">{product.id}</div>
                                </div>
                                <div className="product-actions">
                                    <button className="btn-secondary btn-sm" onClick={() => openEditProduct(product)} disabled={archivingProductId === product.id}>Edit</button>
                                    <button className="btn-danger btn-sm" onClick={() => archiveProduct(product.id)} disabled={archivingProductId === product.id}>
                                        {archivingProductId === product.id ? 'Archiving...' : 'Archive'}
                                    </button>
                                </div>
                            </div>
                            {renderPricesTable(product, null)}
                        </div>
                    ))}
                </div>
            )}

            {/* ── Pricing Configuration tab ─────────────────────────────────── */}
            {tab === 'config' && (
                <div className="config-tab">
                    <div className="form-group">
                        <label>Price Prefix</label>
                        <input
                            type="text"
                            value={configDraft}
                            onChange={e => setConfigDraft(e.target.value)}
                            placeholder="e.g. dev-euw1-vault-"
                        />
                        <p className="field-hint">
                            Used to name and identify default vault products in Stripe.
                            Suggested format: <code>{'{env}-{region}-vault-'}</code>
                            (e.g. <code>dev-euw1-vault-</code>).
                            The lookup key for each price will be <code>{configDraft || '{prefix}'}default-1yr-upfront</code>, etc.
                        </p>
                    </div>
                    <button
                        className="btn-primary"
                        onClick={saveConfig}
                        disabled={configSaving || configDraft === config.price_prefix}
                    >
                        {configSaving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            )}

            {/* Product Modal */}
            {productModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>{productModal.mode === 'add' ? 'Add Product' : 'Edit Product'}</h3>
                        <div className="form-group">
                            <label>Name *</label>
                            <input
                                type="text"
                                value={productModal.name}
                                onChange={e => setProductModal(p => ({ ...p, name: e.target.value }))}
                                placeholder="e.g. Vault by hconnect"
                                autoFocus
                            />
                        </div>
                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                value={productModal.description}
                                onChange={e => setProductModal(p => ({ ...p, description: e.target.value }))}
                                rows={3}
                                placeholder="Optional description shown to customers"
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="btn-secondary" onClick={() => setProductModal(null)}>Cancel</button>
                            <button className="btn-primary" onClick={saveProduct} disabled={saving || !productModal.name.trim()}>
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Price Modal */}
            {priceModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>{priceModal.mode === 'add' ? 'Add Price' : 'Edit Price'}</h3>

                        {priceModal.mode === 'add' ? (
                            <>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Amount *</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={priceModal.amount}
                                            onChange={e => setPriceModal(p => ({ ...p, amount: e.target.value }))}
                                            placeholder="0.00"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Currency *</label>
                                        <CurrencySelect billing value={priceModal.currency} onChange={e => setPriceModal(p => ({ ...p, currency: e.target.value }))} />
                                    </div>
                                </div>
                                {!priceModal.isDefault && (
                                    <div className="form-group">
                                        <label>Billing Period *</label>
                                        <select
                                            value={`${priceModal.interval}|${priceModal.interval_count}`}
                                            onChange={e => {
                                                const found = INTERVALS.find(i => `${i.interval}|${i.interval_count}` === e.target.value);
                                                if (found) setPriceModal(p => ({ ...p, interval: found.interval, interval_count: found.interval_count }));
                                            }}
                                        >
                                            {INTERVALS.map(i => (
                                                <option key={`${i.interval}|${i.interval_count}`} value={`${i.interval}|${i.interval_count}`}>
                                                    {i.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {priceModal.lookup_key && (
                                    <div className="form-group">
                                        <label>Lookup Key</label>
                                        <input type="text" value={priceModal.lookup_key} readOnly className="input-readonly" />
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="edit-price-note">Amount and billing period cannot be changed in Stripe. Archive this price and create a new one to change these values.</p>
                        )}

                        <div className="form-group">
                            <label>Nickname <span className="optional">(optional)</span></label>
                            <input
                                type="text"
                                value={priceModal.nickname}
                                onChange={e => setPriceModal(p => ({ ...p, nickname: e.target.value }))}
                                placeholder="e.g. Annual plan"
                                autoFocus={priceModal.mode === 'edit'}
                            />
                        </div>

                        <div className="modal-actions">
                            <button className="btn-secondary" onClick={() => setPriceModal(null)}>Cancel</button>
                            <button
                                className="btn-primary"
                                onClick={savePrice}
                                disabled={saving || (priceModal.mode === 'add' && (!priceModal.amount || parseFloat(priceModal.amount) <= 0))}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VaultPricing;
